import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MobileBalanceCard from './MobileBalanceCard';
import VoiceRecorderHybrid from './VoiceRecorderWhisper';
import MobileSkeletonLoader from './MobileSkeletonLoader';
import { formatCurrency } from '@/utils/currencyFormat';
import { formatLocalDate } from '@/utils/dateUtils';
import {
  Sparkles, ArrowUpRight, ArrowDownRight, PiggyBank, ChevronRight,
  Calendar, Plus, Minus, Check, CreditCard, Wallet, Percent, Users,
  Eye, EyeOff, Package, ChevronDown, ChevronUp
} from 'lucide-react';

const MobileDashboard = ({ 
  stats, 
  accounts = [], 
  debts = [],
  creditCardsMonthlyDue = [],
  recentTransactions = [], 
  loading = false, 
  selectedPeriod = 'current',
  availableMonths = [],
  latestInsight = null,
  onPeriodChange,
  onRefresh, 
  onAddTransaction 
}) => {
  const navigate = useNavigate();
  const [expandedTxId, setExpandedTxId] = useState(null);
  const [hideBalances, setHideBalances] = useState(() => {
    try { return localStorage.getItem('hideBalances') === 'true'; } catch { return false; }
  });
  const [isCreditCardsCollapsed, setIsCreditCardsCollapsed] = useState(() => {
    try { return localStorage.getItem('hide_credit_cards_monthly_due') === 'true'; } catch { return false; }
  });

  const toggleCreditCardsCollapse = () => {
    setIsCreditCardsCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('hide_credit_cards_monthly_due', String(next)); } catch {}
      return next;
    });
  };

  const toggleHideBalances = () => {
    const nextVal = !hideBalances;
    setHideBalances(nextVal);
    try {
      localStorage.setItem('hideBalances', String(nextVal));
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new CustomEvent('hide-balances-changed', { detail: nextVal }));
    } catch {}
  };

  useEffect(() => {
    const handleSync = () => {
      try { setHideBalances(localStorage.getItem('hideBalances') === 'true'); } catch {}
    };
    window.addEventListener('storage', handleSync);
    window.addEventListener('hide-balances-changed', handleSync);
    return () => {
      window.removeEventListener('storage', handleSync);
      window.removeEventListener('hide-balances-changed', handleSync);
    };
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '¡Buenos días!';
    if (hour < 18) return '¡Buenas tardes!';
    return '¡Buenas noches!';
  };

  if (loading || !stats) {
    return <MobileSkeletonLoader />;
  }

  const netBalance = (stats?.total_income || 0) - (stats?.total_expenses || 0);
  const myDebts = (debts || []).filter(d => d.status === 'active' && d.debt_direction !== 'they_owe_me');
  const theyOweMe = (debts || []).filter(d => d.status === 'active' && d.debt_direction === 'they_owe_me');
  const totalDebtPending = myDebts.reduce((s, d) => s + (d.current_balance || 0), 0);
  const totalReceivablePending = theyOweMe.reduce((s, d) => s + (d.current_balance || 0), 0);
  const loanDebt = totalDebtPending;
  const hasAnyDebts = myDebts.length > 0 || theyOweMe.length > 0;

  // Cuentas con rendimiento activo (Ahorro e Inversión)
  const savingsAssets = (accounts || []).reduce((sum, a) => {
    if (a.type === 'credit_card' || a.is_active === false || !a.has_yield) return sum;
    const amt = a.currency === 'USD' ? (a.current_balance || 0) * 17 : (a.current_balance || 0);
    return sum + amt;
  }, 0);

  // Dinero disponible operativo (cuentas de débito y efectivo sin rendimiento)
  const liquidAssets = (accounts || []).reduce((sum, a) => {
    if (a.type === 'credit_card' || a.is_active === false || a.has_yield) return sum;
    const amt = a.currency === 'USD' ? (a.current_balance || 0) * 17 : (a.current_balance || 0);
    return sum + amt;
  }, 0);

  // Ahorros consolidados (Cuentas con rendimiento + transacciones de ahorro)
  const totalSavings = (stats?.total_savings || 0) + savingsAssets;

  // Deudas en tarjetas de crédito
  const creditCardDebt = (accounts || []).reduce((sum, a) => {
    if (a.type !== 'credit_card' || a.is_active === false) return sum;
    const debt = Math.abs(a.current_balance || 0);
    const amt = a.currency === 'USD' ? debt * 17 : debt;
    return sum + amt;
  }, 0);

  // Deudas consolidadas y Patrimonio Neto Real
  const totalDebt = creditCardDebt + loanDebt;
  const totalAssets = liquidAssets + savingsAssets;
  const patrimonioNetoReal = totalAssets - totalDebt;

  return (
    <div className="space-y-5 pb-10 animate-fadeIn">
      {/* Saludo Premium con Ojito Global */}
      <div className="space-y-1 px-1">
        <div className="flex items-center gap-2 text-blue-600 font-bold tracking-tight">
          <Sparkles className="w-4 h-4 animate-pulse" />
          <span className="uppercase text-[10px] tracking-widest">{getGreeting()}</span>
        </div>
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-black text-slate-900 tracking-tight leading-none">
            Tu Resumen
          </h2>
          <button
            type="button"
            onClick={toggleHideBalances}
            className="w-10 h-10 rounded-2xl bg-white border border-slate-200/80 hover:bg-slate-100 active:scale-95 flex items-center justify-center text-slate-700 transition-all shadow-sm"
            title={hideBalances ? "Mostrar números" : "Ocultar números"}
          >
            {hideBalances ? <EyeOff className="w-5 h-5 text-indigo-600" /> : <Eye className="w-5 h-5 text-slate-700" />}
          </button>
        </div>
      </div>

      {/* Selector Único de Mes */}
      {(() => {
        const currentDate = new Date();
        const prevDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        const previousMonthKey = `${prevDate.getFullYear()}-${prevDate.getMonth() + 1}`;

        return (
          <div className="flex items-center justify-between bg-white border border-slate-200/80 p-2 px-3 rounded-2xl shadow-sm">
            <div className="flex items-center gap-2 text-slate-700 font-bold text-xs">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span>Período:</span>
            </div>
            <div className="relative">
              <select
                value={selectedPeriod}
                onChange={(e) => onPeriodChange && onPeriodChange(e.target.value)}
                className="appearance-none bg-slate-100 text-slate-900 font-bold text-xs px-3 py-1.5 pr-7 rounded-xl border border-slate-200 focus:outline-none cursor-pointer"
              >
                <option value="current">Este mes</option>
                <option value={previousMonthKey}>Mes pasado</option>
                <option value="all">Todo el tiempo</option>
                {availableMonths
                  .filter(m => m.value !== `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}` && m.value !== previousMonthKey)
                  .map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
              </select>
              <ChevronRight className="w-3.5 h-3.5 text-slate-500 absolute right-2 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none" />
            </div>
          </div>
        );
      })()}

      {/* 3 Mini-cards horizontales: Ingresos / Gastos / Ahorros */}
      <div className="grid grid-cols-3 gap-2 px-1">
        <button
          type="button"
          onClick={() => onAddTransaction && onAddTransaction('income')}
          className="p-3 bg-white border border-slate-100 shadow-sm rounded-2xl text-left active:scale-95 transition-transform"
        >
          <div className="w-7 h-7 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-1">
            <Plus className="w-4 h-4" />
          </div>
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Ingresos</p>
          <p className="text-xs font-black text-slate-900 truncate">
            {hideBalances ? '••••••' : formatCurrency(stats?.total_income || 0)}
          </p>
        </button>

        <button
          type="button"
          onClick={() => onAddTransaction && onAddTransaction('expense')}
          className="p-3 bg-white border border-slate-100 shadow-sm rounded-2xl text-left active:scale-95 transition-transform"
        >
          <div className="w-7 h-7 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center mb-1">
            <Minus className="w-4 h-4" />
          </div>
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Gastos</p>
          <p className="text-xs font-black text-slate-900 truncate">
            {hideBalances ? '••••••' : formatCurrency(stats?.total_expenses || 0)}
          </p>
        </button>

        <button
          type="button"
          onClick={() => onAddTransaction && onAddTransaction('saving')}
          className="p-3 bg-white border border-slate-100 shadow-sm rounded-2xl text-left active:scale-95 transition-transform"
        >
          <div className="w-7 h-7 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-1">
            <Check className="w-4 h-4" />
          </div>
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Ahorros</p>
          <p className="text-xs font-black text-slate-900 truncate">
            {hideBalances ? '••••••' : formatCurrency(totalSavings)}
          </p>
        </button>
      </div>

      {/* Tarjeta de Patrimonio Neto Real / Flujo del Período */}
      <MobileBalanceCard 
        patrimonioNeto={patrimonioNetoReal}
        liquidAssets={liquidAssets}
        savingsAssets={savingsAssets}
        totalDebt={totalDebt}
        creditCardDebt={creditCardDebt}
        loanDebt={loanDebt}
        monthlyFlow={netBalance}
        periodIncome={stats?.total_income || 0}
        periodExpense={stats?.total_expenses || 0}
        globalHideBalance={hideBalances}
        onAddClick={() => onAddTransaction('expense')}
      />

      {/* Grabadora de Voz IA - Versión Compacta */}
      <VoiceRecorderHybrid onTransactionCreated={onRefresh} compact={true} />

      {/* SECCIÓN CONDICIONAL: PAGOS DEL MES EN TARJETAS DE CRÉDITO (Colapsable y Horizontal) */}
      {creditCardsMonthlyDue && creditCardsMonthlyDue.length > 0 && (() => {
        const totalCardsDue = creditCardsMonthlyDue.reduce((sum, c) => sum + (c.urgency_status === 'paid' ? 0 : (c.total_due || 0)), 0);
        const unpaidCount = creditCardsMonthlyDue.filter(c => c.urgency_status !== 'paid' && c.current_debt > 0.01).length;

        return (
          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between px-2">
              <button
                type="button"
                onClick={toggleCreditCardsCollapse}
                className="flex items-center gap-1.5 group text-left"
              >
                <div className="w-6 h-6 rounded-lg bg-indigo-100 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                  <Calendar className="w-3.5 h-3.5" />
                </div>
                <div className="flex items-center gap-1.5">
                  <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight">
                    Pagos de Tarjetas
                  </h3>
                  {unpaidCount > 0 && (
                    <span className="text-[10.5px] font-black text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/60 px-2 py-0.5 rounded-full border border-indigo-200/60 dark:border-indigo-800/50">
                      {hideBalances ? '•••' : formatCurrency(totalCardsDue, 'MXN')}
                    </span>
                  )}
                  {isCreditCardsCollapsed ? (
                    <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-transform" />
                  ) : (
                    <ChevronUp className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-transform" />
                  )}
                </div>
              </button>
              <Link to="/accounts" className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-0.5 shrink-0">
                Ver cuentas <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {!isCreditCardsCollapsed && (
              <div className="flex gap-2.5 overflow-x-auto pb-1.5 scrollbar-none no-scrollbar px-1">
                {creditCardsMonthlyDue.map((card) => {
                  const isUrgent = card.urgency_status === 'today' || (card.days_until_due !== null && card.days_until_due <= 3 && card.days_until_due >= 0);
                  const isPaid = card.urgency_status === 'paid' || card.current_debt <= 0.01;

                  return (
                    <div
                      key={card.account_id}
                      className={`w-[265px] shrink-0 snap-start p-3 rounded-2xl border transition-all flex flex-col justify-between ${
                        isPaid
                          ? 'bg-slate-50/80 dark:bg-slate-800/40 border-slate-200/60 dark:border-slate-700/60 opacity-80'
                          : isUrgent
                          ? 'bg-gradient-to-br from-rose-50/80 via-white to-pink-50/40 dark:from-rose-950/30 dark:to-slate-800 border-rose-200/80 dark:border-rose-900/40 shadow-sm'
                          : 'bg-white dark:bg-slate-800 border-slate-200/80 dark:border-slate-700 shadow-sm'
                      }`}
                    >
                      <div>
                        <div className="flex items-start justify-between gap-1">
                          <div className="min-w-0 flex-1">
                            <span className="text-xs font-black text-slate-900 dark:text-white truncate block">
                              {card.account_name}
                            </span>
                            {card.bank_name && (
                              <p className="text-[10px] font-semibold text-slate-400 truncate">
                                {card.bank_name}
                              </p>
                            )}
                          </div>

                          <div className="text-right shrink-0">
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Pago del Mes</p>
                            <p className={`text-sm sm:text-base font-black ${
                              isPaid ? 'text-slate-400 line-through' : 'text-indigo-600 dark:text-indigo-400'
                            }`}>
                              {hideBalances ? '••••••' : formatCurrency(card.total_due, card.currency)}
                            </p>
                          </div>
                        </div>

                        {/* Badge de vencimiento */}
                        <div className="mt-1.5">
                          {isPaid ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-lg border border-emerald-200/60 dark:border-emerald-800/40">
                              <Check className="w-3 h-3" /> Al corriente
                            </span>
                          ) : card.days_until_due !== null ? (
                            <span className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-lg border ${
                              card.days_until_due === 0
                                ? 'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950/60 dark:text-rose-300 animate-pulse'
                                : card.days_until_due <= 3
                                ? 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/60 dark:text-amber-300'
                                : 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300'
                            }`}>
                              <Calendar className="w-3 h-3" />
                              {card.days_until_due === 0 ? '🔴 Vence hoy' : card.days_until_due === 1 ? '⚡ Mañana' : `⏳ En ${card.days_until_due}d`} ({card.due_date_formatted})
                            </span>
                          ) : (
                            <span className="text-[10px] font-medium text-slate-400">
                              Sin fecha límite
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Footer con mini desglose y botón Pagar */}
                      <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between gap-1 text-[10px]">
                        <div className="flex items-center gap-1 overflow-hidden text-slate-500 dark:text-slate-400">
                          {card.msi_month_payment > 0 && (
                            <span className="truncate bg-slate-100 dark:bg-slate-700/60 px-1.5 py-0.5 rounded font-semibold text-slate-700 dark:text-slate-300">
                              MSI: ${parseFloat(card.msi_month_payment).toFixed(0)}
                            </span>
                          )}
                          {card.non_msi_debt > 0 && (
                            <span className="truncate bg-slate-100 dark:bg-slate-700/60 px-1.5 py-0.5 rounded font-semibold text-slate-700 dark:text-slate-300">
                              Contado: ${parseFloat(card.non_msi_debt).toFixed(0)}
                            </span>
                          )}
                          {card.unbroken_adj !== 0 && (
                            <span className="truncate bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded font-semibold border border-amber-200/50">
                              Adj: {card.unbroken_adj > 0 ? '+' : ''}${parseFloat(card.unbroken_adj).toFixed(0)}
                            </span>
                          )}
                        </div>

                        <Link
                          to={`/accounts/${card.account_id}`}
                          className="shrink-0 px-2 py-0.5 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white font-bold rounded-lg text-[10.5px] flex items-center gap-0.5 shadow-xs transition-all"
                        >
                          Pagar <ChevronRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* SECCIÓN CONDICIONAL: CONTROL DE DEUDAS (Debo vs Me deben) */}
      {hasAnyDebts && (
        <div className="space-y-3 pt-1">
          <div className="flex items-center justify-between px-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center">
                <CreditCard className="w-3.5 h-3.5" />
              </div>
              <h3 className="text-base font-black text-slate-900 tracking-tight">Control de Deudas</h3>
            </div>
            <Link to="/debts" className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-0.5">
              Gestionar <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            {/* Card 1: Debo */}
            <Link
              to="/debts?tab=i_owe"
              className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-3xl p-3.5 text-white shadow-lg shadow-rose-500/15 space-y-1 block"
            >
              <p className="text-white/80 text-[10px] font-bold uppercase tracking-wider">🔴 Por Pagar</p>
              <p className="text-xl font-black">{hideBalances ? '••••••' : formatCurrency(totalDebtPending)}</p>
              <p className="text-[10px] text-white/80">{myDebts.length} activa{myDebts.length > 1 ? 's' : ''}</p>
            </Link>

            {/* Card 2: Me deben */}
            <Link
              to="/debts?tab=they_owe_me"
              className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-3xl p-3.5 text-white shadow-lg shadow-emerald-500/15 space-y-1 block"
            >
              <p className="text-white/80 text-[10px] font-bold uppercase tracking-wider">🟢 Por Cobrar</p>
              <p className="text-xl font-black">{hideBalances ? '••••••' : formatCurrency(totalReceivablePending)}</p>
              <p className="text-[10px] text-white/80">{theyOweMe.length} activa{theyOweMe.length > 1 ? 's' : ''}</p>
            </Link>
          </div>
        </div>
      )}

      {/* SECCIÓN CONDICIONAL: TUS CUENTAS (Solo si hay cuentas registradas) */}
      {accounts.length > 0 && (
        <div className="space-y-3 pt-1">
          <div className="flex items-center justify-between px-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center">
                <Wallet className="w-3.5 h-3.5" />
              </div>
              <h3 className="text-base font-black text-slate-900 tracking-tight">Tus Cuentas</h3>
            </div>
            <Link to="/accounts" className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-0.5">
              Ver todas <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-none px-1">
            {accounts.map(acc => (
              <Link
                key={acc.id}
                to={`/accounts/${acc.id}`}
                className="min-w-[140px] flex-1 p-3.5 bg-white border border-slate-100 rounded-2xl shadow-sm flex flex-col justify-between active:scale-95 transition-transform"
              >
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase truncate">{acc.name}</p>
                  <p className="font-black text-slate-900 text-sm mt-0.5">
                    {hideBalances ? '••••••' : formatCurrency(acc.current_balance)}
                  </p>
                </div>
                <span className="text-[9px] font-medium text-indigo-600 mt-2 block">
                  {acc.currency || 'MXN'}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Widget de Últimos Movimientos */}
      <div className="space-y-3 pt-1">
        <div className="flex items-center justify-between px-2">
          <h3 className="text-base font-black text-slate-900 tracking-tight">Últimos Movimientos</h3>
          <Link to="/transactions" className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-0.5">
            Ver todos <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="space-y-2.5">
          {[...recentTransactions]
            .sort((a, b) => {
              const dateA = a.date ? a.date.split('T')[0] : '';
              const dateB = b.date ? b.date.split('T')[0] : '';
              if (dateB !== dateA) return dateB.localeCompare(dateA);
              const timeA = new Date(a.created_at || a.date).getTime() || 0;
              const timeB = new Date(b.created_at || b.date).getTime() || 0;
              return timeB - timeA;
            })
            .slice(0, 5)
            .map((t) => {
            const isIncome = t.type === 'income';
            const isSaving = t.type === 'saving';
            const isExpense = t.type === 'expense';
            const Icon = isIncome ? ArrowUpRight : isSaving ? PiggyBank : ArrowDownRight;
            const colorClass = isIncome ? 'text-emerald-600 bg-emerald-50' : isSaving ? 'text-blue-600 bg-blue-50' : 'text-rose-600 bg-rose-50';
            const acc = accounts.find(a => a.id === t.account_id);
            const isCredit = acc?.type === 'credit_card';
            const showItems = expandedTxId === t.id;

            // Calcular saldo anterior y posterior exacto
            let prevVal = t.previous_balance !== null && t.previous_balance !== undefined ? parseFloat(t.previous_balance) : null;
            let nextVal = t.new_balance !== null && t.new_balance !== undefined ? parseFloat(t.new_balance) : null;
            
            if (isCredit && prevVal !== null) {
              const amt = parseFloat(t.amount) || 0;
              const prevDebt = Math.abs(prevVal);
              prevVal = prevDebt;
              if (t.is_balance_adjustment || /ajuste de saldo/i.test(t.description || '')) {
                nextVal = nextVal !== null ? Math.abs(nextVal) : prevDebt;
              } else if (t.is_breakdown || (t.is_retention && t.retention_status === 'released')) {
                nextVal = prevDebt;
              } else if (isExpense) {
                nextVal = prevDebt + amt; // Compra incrementa la deuda
              } else if (isIncome) {
                nextVal = Math.max(0, prevDebt - amt); // Abono reduce la deuda
              } else {
                nextVal = prevDebt;
              }
            } else if (!isCredit && prevVal !== null && nextVal === null) {
              const amt = parseFloat(t.amount) || 0;
              nextVal = isIncome ? prevVal + amt : isExpense ? prevVal - amt : prevVal;
            }

            const nextColor = isExpense ? 'text-rose-500/90' : 'text-emerald-600/90';

            return (
              <div 
                key={t.id}
                className="p-3.5 bg-white border border-slate-100 rounded-3xl shadow-sm transition-all"
              >
                <div 
                  className="flex items-start gap-3 cursor-pointer"
                  onClick={() => navigate('/transactions')}
                >
                  <div className={`w-10 h-10 rounded-2xl ${colorClass} flex items-center justify-center shrink-0 mt-0.5`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-bold text-sm text-slate-900 truncate">{t.description || 'Movimiento'}</p>
                      <span className={`font-black text-sm shrink-0 ${isIncome ? 'text-emerald-600' : isSaving ? 'text-blue-600' : 'text-rose-600'}`}>
                        {hideBalances ? '••••••' : `${isIncome ? '+' : '-'}${formatCurrency(t.amount)}`}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-2 mt-0.5 flex-wrap">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-[10px] font-medium text-slate-400">
                          {formatLocalDate(t.date, { day: 'numeric', month: 'short' })}
                        </p>
                        {t.items_breakdown?.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedTxId(showItems ? null : t.id);
                            }}
                            className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded-full flex items-center gap-1 active:scale-95 transition-all shadow-xs"
                          >
                            <Package className="w-3 h-3 text-indigo-600" />
                            <span>{t.items_breakdown.length} artículos</span>
                            {showItems ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
                          </button>
                        )}
                      </div>
                      {acc && (
                        <p className="text-[10px] font-medium text-slate-400 truncate max-w-[140px]">
                          {acc.name}
                        </p>
                      )}
                    </div>

                    {/* Fila: Saldo anterior y saldo después */}
                    {prevVal !== null && !isNaN(prevVal) && nextVal !== null && !isNaN(nextVal) && (
                      <div className="mt-2 px-2.5 py-1 bg-slate-50/70 rounded-xl border border-slate-100 flex items-center justify-between text-[10px] gap-1">
                        <div className="flex items-center gap-1 text-slate-400 truncate">
                          <span className="font-normal opacity-80">Ant:</span>
                          <span className="font-medium text-slate-600">
                            {formatCurrency(prevVal)}
                          </span>
                        </div>
                        <span className="text-slate-300 font-normal shrink-0">→</span>
                        <div className="flex items-center gap-1 truncate">
                          <span className="font-normal text-slate-400 opacity-80">Después:</span>
                          <span className={`font-semibold ${nextColor}`}>
                            {formatCurrency(nextVal)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Desglose desplegado de artículos */}
                {showItems && t.items_breakdown?.length > 0 && (
                  <div className="mt-3 pt-2.5 border-t border-slate-100 space-y-1.5">
                    <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                      Artículos de esta compra ({t.items_breakdown.length})
                    </p>
                    <div className="space-y-1">
                      {t.items_breakdown.map((item, idx) => (
                        <div key={item.id || idx} className="flex items-center justify-between text-xs py-1 px-2.5 bg-slate-50 rounded-xl">
                          <span className="text-slate-700 font-medium truncate mr-2">{item.name}</span>
                          <span className="font-bold text-slate-900 shrink-0">${parseFloat(item.price).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {recentTransactions.length === 0 && (
            <div className="p-8 text-center bg-white rounded-3xl border border-dashed border-slate-200">
              <p className="text-xs font-medium text-slate-400">No hay movimientos registrados este mes</p>
            </div>
          )}
        </div>
      </div>

      {/* AI Insights Widget Móvil */}
      <div className="pt-2 px-1 pb-4">
        <div className="p-1 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl shadow-lg shadow-indigo-500/15">
          <div className="bg-white rounded-[1.4rem] p-5 space-y-3.5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-md text-white shrink-0">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <h4 className="font-black text-slate-900 text-base">AI Insights</h4>
                <p className="text-xs text-slate-500 font-medium truncate">Análisis inteligente con IA</p>
              </div>
            </div>

            {/* Resumen ejecutivo */}
            <div className="bg-indigo-50/50 p-3.5 rounded-2xl border border-indigo-100/70 text-xs text-slate-700 leading-relaxed font-medium">
              💡 {latestInsight?.summary || "Análisis predictivo de tus hábitos de gasto y ahorro disponible en Análisis Pro."}
            </div>

            <button
              type="button"
              onClick={() => navigate('/advanced-analytics')}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-md shadow-indigo-600/20 active:scale-95 cursor-pointer"
            >
              <span>Ver análisis completo</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileDashboard;
